import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  Subscription,
  of,
  forkJoin,
  throwError,
  from,
  Subject,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { Auth } from '../models/auth.model';
import { Karyawan } from '../models/pegawai/karyawan.model';
import { storageDict } from './storage/storage-dict';
import { Router } from '@angular/router';
import { catchError, delay, finalize, switchMap, take, tap } from 'rxjs/operators';
import { ClinicEnvironmentService } from './clinic-environment.service';
import { IdbStorageService } from './storage/idb-storage.service';
import { DataKlinikV2 } from '../models/data-klinik.model';
import { DataKlinikDataService } from './data/data-klinik.data.service';
import { KaryawanDataService } from './data/pegawai/karyawan.data.service';
import { ApmService } from '@elastic/apm-rum-angular';
import { HmacSHA256, enc } from 'crypto-js';
import { DataCacheStorageService } from './storage/data-cache-storage.service';
import AuthenticationDataService from './data/go-service/auth-service/authentication.data.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // private fields
  private unsubscribe: Subscription[] = [];
  private api = environment.urlV2;

  // public fields
  public currentUserSubject: BehaviorSubject<Karyawan>;
  public isLoadingSubject: BehaviorSubject<boolean>;
  public currentUser$: Observable<Karyawan>;
  public isLoading$: Observable<boolean>;

  // State to check if the user is logged in or not
  public authState$ = new BehaviorSubject<boolean>(false);

  public idLogStatus: any;
  public idPasien: any;
  public idKaryawan: number;

  // Use logout subject, confirming that logout function only called once.
  private logoutSubject$ = new Subject<void>();
  private logoutSubjectSubscription: Subscription;

  private http: HttpClient;
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private authenticationDataService: AuthenticationDataService,
    private apmService: ApmService,
    private clinicEnvironmentService: ClinicEnvironmentService,
    private idbStorage: IdbStorageService,
    private dataKlinikDataService: DataKlinikDataService,
    private readonly _dataCacheStorageService: DataCacheStorageService,
    private karyawanDataService: KaryawanDataService,
    private router: Router,
    private httpBackend: HttpBackend
  ) {
    this.http = new HttpClient(this.httpBackend);

    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<Karyawan>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    this._initService();
  }

  private async _initService() {
    forkJoin([
      this.idbStorage.getItemSync(storageDict.karyawan),
      this.idbStorage.getItemSync(storageDict.refreshToken),
    ]).subscribe(([karyawan, refreshToken]) => {
      if (karyawan && refreshToken) {
        this.currentUserSubject.next(karyawan);
        this.changeAuthState(true);
        if (this.apmService.apm) {
          this.apmService.apm.setUserContext({ username: karyawan.nama, id: karyawan.id });
        }
      }
    });
    this.subscribeToLogoutSubject();
  }

  private subscribeToLogoutSubject() {
    if (!this.logoutSubjectSubscription || this.logoutSubjectSubscription.closed) {
      this.logoutSubjectSubscription = this.logoutSubject$
        .pipe(
          take(1),
          finalize(() => this.logoutSubjectSubscription.unsubscribe())
        )
        .subscribe(() => {
          this.logout();
        });
    }
  }

  public signIn(payload: {
    username: string;
    password: string;
    clinicUrl: string;
    fcmToken?: string;
  }): Observable<Auth> {
    const userAgent = window.navigator.userAgent;
    this.subscribeToLogoutSubject();
    return this.authenticationDataService.authenticate({
      username: payload.username,
      password: payload.password,
      dataKlinikUrl: payload.clinicUrl,
      fcmToken: payload.fcmToken,
      deviceInfo: userAgent || 'unknown',
    });
  }

  public async logout() {
    if (this.authState$.value === false) return;
    this.changeAuthState(false);

    const refreshToken = await this.idbStorage.getItem(storageDict.refreshToken);

    await this.authenticationDataService
      .signOut(refreshToken)
      .pipe(catchError((err) => of(null)))
      .toPromise();

    await this.idbStorage.clear();
    await this._dataCacheStorageService.clear();
    this.logoutSubjectSubscription.unsubscribe();

    await this.router.navigate(['/auth/login']);
  }

  public logoutGracefully() {
    this.logoutSubject$.next();
  }

  public changePassword(password: string): Observable<any> {
    return this.idbStorage.getItemSync(storageDict.idLogin).pipe(
      switchMap((res: any) =>
        this.http.patch(`${environment.urlV1}/klinik/auth/change-password`, {
          id: res,
          password,
        })
      )
    );
  }

  /** Get authentication state */
  public getAuthState(): Observable<boolean> {
    return this.authState$.asObservable();
  }

  public changeAuthState(val: boolean): void {
    this.authState$.next(val);
    this.clinicEnvironmentService.authState$.next(val);
  }

  public signInWithToken(credentials: {
    token: string;
    fcmToken?: string;
    clinicId: number;
  }): Observable<Karyawan> {
    let tempDataKlinik: number;
    let resultUser: Karyawan;

    return this.http
      .post(
        `${environment.urlV2}/data-klinik/${credentials.clinicId}/klinik/refresh-token/sign-in`,
        {
          refreshToken: credentials.token,
          fcmToken: credentials.fcmToken,
        }
      )
      .pipe(
        switchMap((res: any) => {
          if (res) {
            this.idKaryawan = res.data.idKaryawan;
            tempDataKlinik = res.data.idDataKlinik;
            return forkJoin([
              this.idbStorage.setItemSync(storageDict.authLocalStorageToken, res.data.token),
              this.idbStorage.setItemSync(storageDict.idLogin, res.data.idLogin),
              this.idbStorage.setItemSync(storageDict.refreshToken, res.data.refreshToken),
            ]);
          } else {
            return throwError(new Error('token invalid'));
          }
        }),
        delay(250),
        switchMap(() => this.karyawanDataService.getById(this.idKaryawan)),
        tap((res: Karyawan) => this.idbStorage.setItem(storageDict.karyawan, res)),
        tap((res: Karyawan) => this.currentUserSubject.next(res)),
        tap((res: Karyawan) => (resultUser = res)),
        switchMap(() => this.dataKlinikDataService.getById()),
        tap((res: DataKlinikV2) => this.idbStorage.setItem(storageDict.klinik, res)),
        switchMap((res) => {
          if (res.sharingPasien !== 0) {
            return this.dataKlinikDataService.getDataHeadOffice(res.idGroupDataKlinik);
          } else {
            return of(null);
          }
        }),
        tap((res: DataKlinikV2) => this.idbStorage.setItem(storageDict.headOffice, res)),
        switchMap(() => this.dataKlinikDataService.getById(tempDataKlinik)),
        switchMap((res: DataKlinikV2) => from(this.clinicEnvironmentService.setClinic(res))),
        tap(() => this.changeAuthState(true)),
        switchMap(() => of(resultUser))
      ) as Observable<Karyawan>;
  }

  public getNewToken(): Observable<{ token: string }> {
    return forkJoin([
      this.idbStorage.getItemSync(storageDict.idLogin),
      this.idbStorage.getItemSync(storageDict.refreshToken),
    ]).pipe(
      switchMap((res: [number, string]) =>
        this.authenticationDataService.refreshToken(res[0], res[1])
      ),
      catchError((err) => throwError(err))
    );
  }

  public generateSignature() {
    const signature = HmacSHA256(
      environment.apiId + ':' + environment.apiPass,
      environment.apiSecret
    );
    const encodedSignature = signature.toString(enc.Base64);
    return encodedSignature;
  }
}
